/**
 * A message on the clipboard, words and picture both.
 *
 * Takes a resolved address rather than a MediaRef, the way the viewer does, so
 * this stays clear of the data layer — and so it can be tested without
 * standing up Firebase to ask what a media key turns into.
 */

/**
 * PNG, because it is the one image type every browser will accept on the
 * clipboard. Everything this app stores is a JPEG, so it goes through a canvas
 * on the way.
 */
async function pngOf(src: string): Promise<Blob> {
  /*
   * The same three options the download uses, for the same reason: the gateway
   * authorises by cookie, so the fetch has to carry it, and a cached response
   * put there by an <img> was served no CORS headers for a cors fetch to check
   * itself against. `reload` goes past that entry and replaces it.
   */
  const response = await fetch(src, {
    credentials: 'include',
    mode: 'cors',
    cache: 'reload',
  });
  if (!response.ok) throw new Error(`could not fetch media: ${response.status}`);

  const blob = await response.blob();
  if (blob.type === 'image/png') return blob;

  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext('2d')?.drawImage(bitmap, 0, 0);
  bitmap.close();

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (out) => (out ? resolve(out) : reject(new Error('could not encode the picture'))),
      'image/png',
    );
  });
}

/**
 * One clipboard item carrying both representations where the message has both:
 * pasting into Messages gives the photograph, pasting into a text field gives
 * what was said. That is what the clipboard was built for, and most apps only
 * ever put one of the two on it.
 *
 * The picture is handed over as a promise rather than fetched first. Safari
 * only allows a write inside the gesture that asked for it, and a fetch that
 * finishes after that has already missed its turn — passing the promise is
 * what holds the slot open.
 */
export async function copyMessage(text: string | undefined, src?: string | null): Promise<void> {
  const words = (text ?? '').trim();

  // No picture, or a browser too old to take one: the words are the message.
  if (!src || typeof ClipboardItem !== 'function' || !navigator.clipboard?.write) {
    await navigator.clipboard.writeText(words);
    return;
  }

  const parts: Record<string, Promise<Blob>> = { 'image/png': pngOf(src) };
  if (words) parts['text/plain'] = Promise.resolve(new Blob([words], { type: 'text/plain' }));

  try {
    await navigator.clipboard.write([new ClipboardItem(parts)]);
  } catch (err) {
    // Refused the picture, or the encode failed. Something is better than a
    // button that did nothing, so fall back to what was said.
    if (!words) throw err;
    await navigator.clipboard.writeText(words);
  }
}
